import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter';
import { faSliders } from '@fortawesome/free-solid-svg-icons/faSliders';
import { StyledFontAwesomeIconMui5 } from 'components/atoms/FontAwesomeIcon';
import {
  SelectControl,
  SelectField,
  SelectLabel,
  SelectOption,
} from 'components/atoms/Select';

import { faMoneyBills } from '@fortawesome/free-solid-svg-icons/faMoneyBills';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Theme as ThemeMui5 } from '@mui/material/styles/createTheme';
import { SxProps } from '@mui/system';
import { NoSSR } from 'components/atoms/NoSSR';
import { scanModeInputAtom } from 'containers/deal/recoil/atoms';
import { useCityByZipcode } from 'hooks/useCityByZipcode';
import { useScreenSize } from 'hooks/useScreenSize';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { FC } from 'react';
import { useRecoilValue } from 'recoil';
import { useFilters } from 'services/filters/hooks/useFilters';
import { filterZipcodeAtom } from 'services/zipcode/recoil';
import {
  activeSortingOptions,
  completedSortingOptions,
} from 'shared/config/sortingOptions';
import { getStateByZip } from 'shared/services/geo';
import { SortType } from 'shared/types/endpoints';
import { MSAPIScanMode } from 'shared/types/marketScan';
import { green } from '../../../theme/colors';

const Header = styled('div')(
  ({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing(4)};`,
);

const FilterIcon = styled(StyledFontAwesomeIconMui5)(
  ({ theme }) => `
  margin-right: ${theme.spacing(1)};`,
);

const ListTitle = styled(Typography)(
  ({ theme }) => `
  &.MuiTypography-root {
    font-size: 1.8rem;
    ${theme.breakpoints.down('lg')} {
      font-size: 1.4rem;
      max-width: calc(100% - ${theme.spacing(8)}px);
    }
  }`,
);

const ListSubtitle = styled(Typography)(
  ({ theme }) => `
   &.MuiTypography-root {
     ${theme.breakpoints.down('lg')} {
       max-width: calc(100% - ${theme.spacing(8)}px);
     }
   }`,
);

const generateFixedBarStyles = (
  theme: ThemeMui5,
  isSticky?: boolean,
): string => {
  if (isSticky) {
    return `
    ${theme.breakpoints.down('md')} {
      position: fixed;
      z-index: 1299;
      top: 65px;
      right: ${theme.spacing(2)};
      left: ${theme.spacing(2)};
      box-shadow: ${theme.shadows[2]};
    }
    ${theme.breakpoints.up('sm')} {
      right: ${theme.spacing(3)};
      left: ${theme.spacing(3)};
    }`;
  }
  return '';
};

const FilterBar = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isSticky',
})<{
  isSticky?: boolean;
}>(
  ({ theme, isSticky }) => `
  margin: ${theme.spacing(0, -2)};
  padding: ${theme.spacing(0, 2)};
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  border-top: 1px solid ${theme.palette.divider};
  border-bottom: 1px solid ${theme.palette.divider};
  background: ${theme.palette.common.white};
  ${theme.breakpoints.up('sm')} {
    margin: ${theme.spacing(0, -3)};
    padding: ${theme.spacing(0, 3)};
  }
  ${theme.breakpoints.up('md')} {
    background: none;
    margin: 0;
    border: 0;
    padding: 0;
    justify-content: flex-end;
  }
  ${generateFixedBarStyles(theme, isSticky)}`,
);

const ButtonContainer = styled('div')(
  ({ theme }) => `
  display: flex;
  align-items: center;
  flex-direction: column;
  border-right: 1px solid ${theme.palette.divider};
  padding-right: ${theme.spacing(1)};
  margin-right: ${theme.spacing(1)};
  margin-left: ${theme.spacing(-1)};
  ${theme.breakpoints.up('sm')} {
    padding-right: ${theme.spacing(2)};
  }
  ${theme.breakpoints.up('md')} {
    display: none;
  }`,
);

const SortByContainer = styled('div')(
  ({ theme }) => `
  display: flex;
  align-items: center;
  padding: ${theme.spacing(1, 0)};
  ${theme.breakpoints.down('md')} {
    width: 100%;
    justify-content: space-between;
  }
  & .MuiFormControl-root {
    margin-top: 0 !important;
    min-width: 200px;
    ${theme.breakpoints.down('md')} {
      flex: 1;
    }
  }
  & .MuiFormLabel-root.MuiInputLabel-root {
    width: 95px;
    transform: none !important;
  }`,
);

const greenBadgeStyles: SxProps<ThemeMui5> = (t) => ({
  display: 'inline-flex',
  padding: t.spacing(0, 2),
  border: `2px solid ${green[300]}`,
});

const ListBadge: FC = () => (
  <Box my={1}>
    <Alert
      icon={<StyledFontAwesomeIconMui5 icon={faMoneyBills} />}
      variant='standard'
      severity='success'
      sx={greenBadgeStyles}
    >
      <Typography color='inherit' variant='body2'>
        <b>The price you see is the price you get!</b>
      </Typography>
    </Alert>
  </Box>
);

type DealsToolbarProps = {
  auctionsCount: number;
  onFiltersClick: () => void;
  sticky?: boolean;
  zipcode: string;
};

function useSearchTitle(
  zipcode: string,
  query: ParsedUrlQuery,
  count: number,
): string {
  const filterZipcode = useRecoilValue(filterZipcodeAtom);
  const effectiveZipcode = zipcode || filterZipcode;

  const city = useCityByZipcode(effectiveZipcode);
  const { state } = getStateByZip(effectiveZipcode);

  const scanMode = useRecoilValue(scanModeInputAtom);
  const isFinance = scanMode === MSAPIScanMode.Retail;

  const stateName = state ? `in ${state} ` : '';
  const cityName = city ? `in ${city} ` : '';
  const locationName =
    city && state ? `in ${city}, ${state} ` : `${stateName}${cityName}`;

  const { make, model } = query;
  const makeName = make ? `${make} ` : '';
  const modelName = model ? `${model} ` : '';
  const carNameholder =
    make || model || !locationName
      ? ''
      : `New Vehicle${count !== 1 ? 's' : ''} `;
  const vehicleName = `${carNameholder}${makeName.toUpperCase()}${modelName.toUpperCase()}`;

  if (!vehicleName && !locationName) {
    const today = new Date();
    const month = today.toLocaleString('en', { month: 'long' });
    return `Best Car ${
      isFinance ? 'Finance' : 'Lease'
    } Deals in ${month} ${today.getFullYear()}`;
  }

  return `${count} Lease or Buy a ${vehicleName}${locationName}`;
}

const DealsToolbar: FC<DealsToolbarProps> = ({
  auctionsCount,
  onFiltersClick,
  sticky,
  zipcode,
}): JSX.Element => {
  const router = useRouter();
  const { filters, setFilterValue, filtersCount } = useFilters();
  const title = useSearchTitle(zipcode, router.query, auctionsCount);

  const isCompleted = router.query.completed === 'true';

  const sortingOptionsByStatus = isCompleted
    ? completedSortingOptions
    : activeSortingOptions;

  const sortType = filters.sortType || SortType.PriceLowHigh;

  const { isExtraSmallScreen, isSmallScreen } = useScreenSize();

  const isNarrowScreen = isExtraSmallScreen || isSmallScreen;

  return (
    <Header>
      <Grid container>
        <Grid
          item
          xs={12}
          sm={12}
          md={8}
          sx={{ display: isNarrowScreen ? 'none' : 'block' }}
        >
          {/* TODO: FIX City for SEO */}
          <ListTitle variant='h1' suppressHydrationWarning>
            {title}
          </ListTitle>
          <ListBadge />
          <ListSubtitle variant='body2'>
            Discover our latest specials for both leasing and financing,
            offering flexible terms, affordable pricing, and great deals on
            specific models.
          </ListSubtitle>
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={4}
          alignContent='flex-end'
          alignItems='flex-end'
        >
          <FilterBar isSticky={sticky}>
            <ButtonContainer>
              <IconButton
                color='primary'
                onClick={() => onFiltersClick()}
                sx={(t) => ({
                  '&.MuiButtonBase-root': {
                    borderRadius: '5px',
                    padding: t.spacing(1),
                    marginTop: t.spacing(0.5),
                  },
                })}
              >
                <Badge
                  overlap='rectangular'
                  badgeContent={filtersCount}
                  color='secondary'
                >
                  <StyledFontAwesomeIconMui5 icon={faSliders} size='lg' />
                </Badge>
                <Box padding={'4px'} ml={'4px'}>
                  <Typography
                    variant='h5'
                    component={'p'}
                    sx={{ '&.MuiTypography-root': { fontWeight: 'normal' } }}
                  >
                    Filters
                  </Typography>
                </Box>
              </IconButton>
            </ButtonContainer>
            <SortByContainer>
              <NoSSR>
                {isExtraSmallScreen ? null : (
                  <SelectLabel>
                    <FilterIcon icon={faFilter} color='primary' />
                    Sort by
                  </SelectLabel>
                )}
              </NoSSR>
              <SelectControl>
                <SelectField
                  variant='outlined'
                  displayEmpty
                  onChange={(e) => {
                    setFilterValue('sortType', e.target.value as SortType);
                  }}
                  value={sortType}
                  defaultValue={SortType.PriceLowHigh}
                >
                  {sortingOptionsByStatus.map((option) => (
                    <SelectOption key={option.key} value={option.key}>
                      {option.label}
                    </SelectOption>
                  ))}
                </SelectField>
              </SelectControl>
            </SortByContainer>
          </FilterBar>
          {/* <Hidden mdUp>{sticky && <div style={{ height: 67 }} />}</Hidden> */}
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={8}
          sx={{ display: isNarrowScreen ? 'block' : 'none' }}
          mt={1}
        >
          <ListTitle variant='h1' suppressHydrationWarning>
            {title}
          </ListTitle>
          <ListBadge />
          <ListSubtitle variant='body2'>
            Discover our latest specials for both leasing and financing,
            offering flexible terms, affordable pricing, and great deals on
            specific models.
          </ListSubtitle>
        </Grid>
      </Grid>
    </Header>
  );
};

export default DealsToolbar;
